import { useState, useEffect, useMemo } from "react";
import {
  MagnifyingGlassIcon,
  UserGroupIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import toast from "react-hot-toast";
import PageHeader from "../../components/layout/PageHeader";
import { Avatar } from "../../components/ui/Avatar";
import { Badge } from "../../components/ui/Badge";
import { Button } from "../../components/ui/Button";
import { Input } from "../../components/ui/Input";
import { ConfirmDialog } from "../../components/ui/ConfirmDialog";
import { useSeason } from "../../context/SeasonContext";
import { useDebounce } from "../../hooks/useDebounce";
import { campersApi } from "../../api/campers";
import { adminApi } from "../../api/admin";
import { reportsApi } from "../../api/reports";
import type {
  Camper,
  User,
  CamperTeam,
  TeamCount,
  TeamReportData,
  TeamDivisionRow,
  TeamCabinRow,
} from "../../types";

// ─── Types ────────────────────────────────────────────────────────────────────

type PersonType = "camper" | "staff";

interface AssignablePerson {
  type: PersonType;
  id: number;
  firstName: string;
  lastName: string;
  cabin?: string;
  cabinId?: number;
  division?: string;
  divisionId?: number;
  role?: string;
  currentTeam?: CamperTeam | null;
}

interface FamilyMember {
  type: PersonType;
  id: number;
  firstName: string;
  lastName: string;
  team: CamperTeam | null | undefined;
  /** For campers: 2-digit years attended, most-recent first (e.g. "26, 25"). */
  years?: string;
  role?: string;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

// Suggest team to keep Army/Navy balanced across overall, division, and cabin.
// Most-specific imbalance wins: cabin > division > overall. Ties fall through.
function suggestTeam(levels: Array<TeamCount | undefined>): CamperTeam {
  for (const row of levels) {
    if (!row) continue;
    if (row.army < row.navy) return "army";
    if (row.navy < row.army) return "navy";
  }
  return Math.random() < 0.5 ? "army" : "navy";
}

function camperToPerson(c: Camper): AssignablePerson {
  const enrollment = c.enrollments?.[0];
  const cabin = enrollment?.cabin?.name;
  const cabinId = enrollment?.cabin?.id ?? enrollment?.cabinId ?? undefined;
  const division =
    enrollment?.division?.name ??
    enrollment?.cabin?.cabinGroup?.division?.name ??
    undefined;
  const divisionId =
    enrollment?.divisionId ??
    enrollment?.cabin?.cabinGroup?.division?.id ??
    undefined;
  return {
    type: "camper",
    id: c.id,
    firstName: c.firstName,
    lastName: c.lastName,
    cabin,
    cabinId: cabinId ?? undefined,
    division,
    divisionId,
    currentTeam: c.team,
  };
}

function staffToPerson(u: User): AssignablePerson {
  return {
    type: "staff",
    id: u.id,
    firstName: u.firstName,
    lastName: u.lastName,
    role: u.role,
    currentTeam: u.team,
  };
}

function matchesSearch(person: { firstName: string; lastName: string }, q: string): boolean {
  if (!q) return true;
  const full = `${person.firstName} ${person.lastName}`.toLowerCase();
  return full.includes(q.toLowerCase());
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function TeamMiniBar({ army, navy, unassigned, total, label }: {
  army: number; navy: number; unassigned: number; total: number; label: string;
}) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-stone-500 font-medium w-24 truncate">{label}</span>
      <div className="flex items-center gap-2">
        <span className="inline-flex items-center gap-1">
          <Badge label="A" color="army" size="sm" />
          <span className="tabular-nums font-semibold text-army-700">{army}</span>
        </span>
        <span className="inline-flex items-center gap-1">
          <Badge label="N" color="navy" size="sm" />
          <span className="tabular-nums font-semibold text-navy-700">{navy}</span>
        </span>
        {unassigned > 0 && (
          <span className="text-stone-400 tabular-nums text-xs">+{unassigned} unassigned</span>
        )}
        <span className="text-stone-400 tabular-nums text-xs">/ {total}</span>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function TeamAssignment() {
  const { selectedTermId, selectedYear } = useSeason();

  // Loaded data
  const [reportData, setReportData] = useState<TeamReportData | null>(null);
  const [unassignedCampers, setUnassignedCampers] = useState<Camper[]>([]);
  const [allStaff, setAllStaff] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);

  // UI state
  const [mode, setMode] = useState<PersonType>("camper");
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 300);
  const [selected, setSelected] = useState<AssignablePerson | null>(null);
  const [assigning, setAssigning] = useState(false);
  const [pendingTeam, setPendingTeam] = useState<CamperTeam | null>(null);

  // Family search
  const [familySearch, setFamilySearch] = useState("");
  const debouncedFamily = useDebounce(familySearch, 300);
  const [familyResults, setFamilyResults] = useState<FamilyMember[]>([]);
  const [familyLoading, setFamilyLoading] = useState(false);

  // Load data when term changes
  useEffect(() => {
    setLoading(true);
    setSelected(null);
    const camperPromise = selectedTermId
      ? campersApi.list({
          team: "unassigned",
          termId: selectedTermId,
          year: selectedYear,
          pageSize: 500,
        })
      : campersApi.list({ team: "unassigned", year: selectedYear, pageSize: 500 });

    const reportPromise = selectedTermId
      ? reportsApi.teamSummary(selectedTermId, selectedYear)
      : Promise.resolve(null as TeamReportData | null);

    Promise.all([camperPromise, adminApi.listUsers(), reportPromise])
      .then(([camperPage, staff, report]) => {
        setUnassignedCampers(camperPage.data);
        setAllStaff(staff);
        setReportData(report);
      })
      .catch(() => toast.error("Failed to load team assignment data"))
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTermId, selectedYear]);

  // Family search — searches across all campers (any term, any year) and all
  // staff, so a father/son (or staff parent + camper child) attending
  // different terms or years end up on the same team.
  useEffect(() => {
    if (!debouncedFamily || debouncedFamily.trim().length < 2) {
      setFamilyResults([]);
      return;
    }

    const query = debouncedFamily.toLowerCase();
    const staffMatches = allStaff.filter(
      (s) => s.team != null && s.lastName.toLowerCase().includes(query)
    );
    const staffResults = staffMatches.map((s): FamilyMember => ({
      type: "staff",
      id: s.id,
      firstName: s.firstName,
      lastName: s.lastName,
      team: s.team,
      role: s.role,
    }));

    setFamilyLoading(true);
    // No year scoping: backend returns the camper's full enrollment history,
    // which lets us render every year they've attended.
    campersApi
      .list({ search: debouncedFamily, pageSize: 30 })
      .then((page) => {
        const withTeam = page.data.filter((c) => c.team != null);
        setFamilyResults([
          ...withTeam.map((c): FamilyMember => {
            const years = Array.from(
              new Set((c.enrollments ?? []).map((e) => e.year))
            )
              .sort((a, b) => b - a)
              .map((y) => String(y).slice(-2))
              .join(", ");
            return {
              type: "camper",
              id: c.id,
              firstName: c.firstName,
              lastName: c.lastName,
              team: c.team,
              years: years || undefined,
            };
          }),
          ...staffResults,
        ]);
      })
      .catch(() => {
        // Camper search failed — still show staff matches so search isn't empty.
        setFamilyResults(staffResults);
      })
      .finally(() => setFamilyLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedFamily]);

  // Pre-populate family search when a person is selected
  useEffect(() => {
    if (selected) {
      setFamilySearch(selected.lastName);
    } else {
      setFamilySearch("");
      setFamilyResults([]);
    }
  }, [selected]);

  // Derived lists
  const filteredCampers = useMemo(
    () => unassignedCampers.filter((c) => matchesSearch(c, debouncedSearch)),
    [unassignedCampers, debouncedSearch]
  );

  const unassignedStaff = useMemo(
    () => allStaff.filter((s) => s.active && !s.team),
    [allStaff]
  );

  const filteredStaff = useMemo(
    () => unassignedStaff.filter((s) => matchesSearch(s, debouncedSearch)),
    [unassignedStaff, debouncedSearch]
  );

  // Context data for selected person
  const divisionRow: TeamDivisionRow | undefined = useMemo(() => {
    if (!reportData || !selected) return undefined;
    const byId = selected.divisionId
      ? reportData.byDivision.find((d) => d.division.id === selected.divisionId)
      : undefined;
    if (byId) return byId;
    if (selected.division) {
      return reportData.byDivision.find((d) => d.division.name === selected.division);
    }
    return undefined;
  }, [selected, reportData]);

  const cabinRow: TeamCabinRow | undefined = useMemo(() => {
    if (!selected?.cabinId || !reportData) return undefined;
    return reportData.byCabin.find((c) => c.cabin.id === selected.cabinId);
  }, [selected, reportData]);

  // For staff, suggest based on staff balance; for campers, balance across
  // cabin → division → overall (most-specific imbalance wins).
  const suggestion = useMemo(() => {
    if (selected?.type === "staff" && reportData?.staff) {
      return suggestTeam([reportData.staff]);
    }
    return suggestTeam([cabinRow, divisionRow, reportData?.overall]);
  }, [selected?.type, cabinRow, divisionRow, reportData]);

  // Assignment handler
  const handleAssign = async (team: CamperTeam) => {
    if (!selected) return;

    // If already has a team, show confirm dialog
    if (selected.currentTeam && selected.currentTeam !== team) {
      setPendingTeam(team);
      return;
    }

    await doAssign(team);
  };

  const doAssign = async (team: CamperTeam) => {
    if (!selected) return;
    setAssigning(true);
    try {
      if (selected.type === "camper") {
        await campersApi.setTeam(selected.id, team);
        setUnassignedCampers((prev) => prev.filter((c) => c.id !== selected.id));
      } else {
        await adminApi.setUserTeam(selected.id, team);
        setAllStaff((prev) =>
          prev.map((s) => (s.id === selected.id ? { ...s, team } : s))
        );
      }
      // Refresh report data to keep context counts accurate
      if (selectedTermId) {
        reportsApi.teamSummary(selectedTermId, selectedYear).then(setReportData).catch(() => {});
      }
      const teamLabel = team === "army" ? "Army" : "Navy";
      toast.success(`${selected.firstName} ${selected.lastName} assigned to ${teamLabel}`);
      setSelected(null);
    } catch {
      toast.error("Failed to assign team");
    } finally {
      setAssigning(false);
      setPendingTeam(null);
    }
  };

  // Tab counts
  const camperCount = unassignedCampers.length;
  const staffCount = unassignedStaff.length;

  const currentList = mode === "camper" ? filteredCampers : filteredStaff;

  return (
    <div>
      <PageHeader
        title="Team Assignment"
        subtitle="Assign Army or Navy teams to campers and staff"
      />

      <div className="p-6 lg:p-8">
        {/* Tabs */}
        <div className="flex gap-1 mb-6 border-b border-stone-200">
          <button
            onClick={() => { setMode("camper"); setSelected(null); setSearch(""); }}
            className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
              mode === "camper"
                ? "bg-white border border-b-white border-stone-200 -mb-px text-forest-700"
                : "text-stone-500 hover:text-stone-700"
            }`}
          >
            Campers
            {camperCount > 0 && (
              <span className="ml-2 inline-flex items-center justify-center w-5 h-5 rounded-full bg-amber-100 text-amber-700 text-xs font-bold">
                {camperCount > 99 ? "99+" : camperCount}
              </span>
            )}
          </button>
          <button
            onClick={() => { setMode("staff"); setSelected(null); setSearch(""); }}
            className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
              mode === "staff"
                ? "bg-white border border-b-white border-stone-200 -mb-px text-forest-700"
                : "text-stone-500 hover:text-stone-700"
            }`}
          >
            Staff
            {staffCount > 0 && (
              <span className="ml-2 inline-flex items-center justify-center w-5 h-5 rounded-full bg-amber-100 text-amber-700 text-xs font-bold">
                {staffCount > 99 ? "99+" : staffCount}
              </span>
            )}
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-4 border-forest-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="flex gap-6 min-h-[500px]">
            {/* Left panel — unassigned list */}
            <div className="w-72 flex-shrink-0 flex flex-col gap-3">
              <Input
                placeholder={`Search ${mode === "camper" ? "campers" : "staff"}…`}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                leftIcon={<MagnifyingGlassIcon className="w-4 h-4" />}
              />

              {currentList.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <UserGroupIcon className="w-10 h-10 text-stone-300 mb-3" />
                  <p className="text-sm text-stone-400">
                    {debouncedSearch
                      ? "No matches found"
                      : `No unassigned ${mode === "camper" ? "campers" : "staff"}`}
                  </p>
                </div>
              ) : (
                <div className="overflow-y-auto max-h-[600px] rounded-lg border border-stone-200 divide-y divide-stone-100 bg-white">
                  {currentList.map((person) => {
                    const p = mode === "camper"
                      ? camperToPerson(person as Camper)
                      : staffToPerson(person as User);
                    const isSelected = selected?.id === p.id && selected?.type === p.type;
                    return (
                      <button
                        key={`${p.type}-${p.id}`}
                        onClick={() => setSelected(isSelected ? null : p)}
                        className={`w-full text-left px-3 py-2.5 flex items-center gap-2.5 transition-colors ${
                          isSelected
                            ? "bg-forest-50 border-l-2 border-forest-500"
                            : "hover:bg-stone-50"
                        }`}
                      >
                        <Avatar firstName={p.firstName} lastName={p.lastName} size="sm" />
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-stone-900 truncate">
                            {p.firstName} {p.lastName}
                          </p>
                          <p className="text-xs text-stone-400 truncate">
                            {p.type === "camper"
                              ? [p.division, p.cabin].filter(Boolean).join(" · ") || "No enrollment"
                              : p.role}
                          </p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Right panel — assignment detail */}
            {selected ? (
              <div className="flex-1 min-w-0">
                <div className="bg-white rounded-xl border border-stone-200 p-6 space-y-6">
                  {/* Header */}
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <Avatar firstName={selected.firstName} lastName={selected.lastName} size="md" />
                      <div>
                        <h2 className="text-lg font-semibold text-stone-900">
                          {selected.firstName} {selected.lastName}
                        </h2>
                        <p className="text-sm text-stone-500">
                          {selected.type === "camper"
                            ? [selected.division, selected.cabin].filter(Boolean).join(" · ") || "No enrollment"
                            : selected.role}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => setSelected(null)}
                      className="text-stone-400 hover:text-stone-600 transition-colors"
                    >
                      <XMarkIcon className="w-5 h-5" />
                    </button>
                  </div>

                  {/* Current team (if any) */}
                  {selected.currentTeam && (
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-stone-500">Current team:</span>
                      <Badge
                        label={selected.currentTeam === "army" ? "Army" : "Navy"}
                        color={selected.currentTeam}
                      />
                    </div>
                  )}

                  {/* Camper balance — overall, division, cabin */}
                  {selected.type === "camper" && reportData && (
                    <div className="space-y-2">
                      <h3 className="text-xs font-semibold text-stone-500 uppercase tracking-wide">
                        Current Balance
                      </h3>
                      <div className="bg-stone-50 rounded-lg p-3 space-y-2">
                        <TeamMiniBar
                          label="Term"
                          army={reportData.overall.army}
                          navy={reportData.overall.navy}
                          unassigned={reportData.overall.unassigned}
                          total={reportData.overall.total}
                        />
                        {divisionRow && (
                          <TeamMiniBar
                            label={divisionRow.division.name}
                            army={divisionRow.army}
                            navy={divisionRow.navy}
                            unassigned={divisionRow.unassigned}
                            total={divisionRow.total}
                          />
                        )}
                        {cabinRow && (
                          <TeamMiniBar
                            label={cabinRow.cabin.name}
                            army={cabinRow.army}
                            navy={cabinRow.navy}
                            unassigned={cabinRow.unassigned}
                            total={cabinRow.total}
                          />
                        )}
                      </div>
                    </div>
                  )}

                  {/* Staff balance */}
                  {selected.type === "staff" && reportData?.staff && (
                    <div className="space-y-2">
                      <h3 className="text-xs font-semibold text-stone-500 uppercase tracking-wide">
                        Staff Balance
                      </h3>
                      <div className="bg-stone-50 rounded-lg p-3">
                        <TeamMiniBar
                          label="All staff"
                          army={reportData.staff.army}
                          navy={reportData.staff.navy}
                          unassigned={reportData.staff.unassigned}
                          total={reportData.staff.total}
                        />
                      </div>
                    </div>
                  )}

                  {/* Suggested team */}
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-stone-500">Suggested:</span>
                    <Badge
                      label={suggestion === "army" ? "Army (balanced)" : "Navy (balanced)"}
                      color={suggestion}
                    />
                  </div>

                  {/* Family search */}
                  <div className="space-y-2">
                    <h3 className="text-xs font-semibold text-stone-500 uppercase tracking-wide">
                      Family Team Search
                    </h3>
                    <Input
                      placeholder="Search by last name…"
                      value={familySearch}
                      onChange={(e) => setFamilySearch(e.target.value)}
                      leftIcon={<MagnifyingGlassIcon className="w-4 h-4" />}
                    />
                    {familyLoading && (
                      <p className="text-xs text-stone-400">Searching…</p>
                    )}
                    {!familyLoading && familyResults.length > 0 && (
                      <div className="rounded-lg border border-stone-200 divide-y divide-stone-100 bg-white max-h-48 overflow-y-auto">
                        {familyResults.map((m) => (
                          <div
                            key={`${m.type}-${m.id}`}
                            className="flex items-center justify-between px-3 py-2"
                          >
                            <div className="flex items-center gap-2 min-w-0">
                              <Avatar firstName={m.firstName} lastName={m.lastName} size="sm" />
                              <div className="min-w-0">
                                <p className="text-sm text-stone-800 truncate">
                                  {m.firstName} {m.lastName}
                                </p>
                                <p className="text-xs text-stone-400 truncate">
                                  {m.type === "camper"
                                    ? m.years
                                      ? `Attended ${m.years}`
                                      : "Camper"
                                    : m.role}
                                </p>
                              </div>
                            </div>
                            {m.team ? (
                              <Badge
                                label={m.team === "army" ? "Army" : "Navy"}
                                color={m.team}
                              />
                            ) : (
                              <span className="text-xs text-stone-400">Unassigned</span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    {!familyLoading &&
                      debouncedFamily.length >= 2 &&
                      familyResults.length === 0 && (
                        <p className="text-xs text-stone-400">
                          No family members with team assignments found.
                        </p>
                      )}
                  </div>

                  {/* Assign buttons */}
                  <div className="space-y-2">
                    <h3 className="text-xs font-semibold text-stone-500 uppercase tracking-wide">
                      Assign Team
                    </h3>
                    <div className="flex gap-3">
                      <button
                        disabled={assigning}
                        onClick={() => handleAssign("army")}
                        className={`flex-1 py-3 rounded-xl font-semibold text-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed
                          bg-army-100 hover:bg-army-200 active:bg-army-300 text-army-800 border-2 border-army-200
                          ${suggestion === "army" ? "ring-2 ring-army-400 ring-offset-1" : ""}
                        `}
                      >
                        {assigning ? "Assigning…" : "⚔ Army"}
                      </button>
                      <button
                        disabled={assigning}
                        onClick={() => handleAssign("navy")}
                        className={`flex-1 py-3 rounded-xl font-semibold text-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed
                          bg-navy-100 hover:bg-navy-200 active:bg-navy-300 text-navy-800 border-2 border-navy-200
                          ${suggestion === "navy" ? "ring-2 ring-navy-400 ring-offset-1" : ""}
                        `}
                      >
                        {assigning ? "Assigning…" : "⚓ Navy"}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <UserGroupIcon className="w-12 h-12 text-stone-200 mx-auto mb-3" />
                  <p className="text-stone-400 text-sm">
                    Select a person from the list to assign their team
                  </p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Confirm dialog for reassignment */}
      <ConfirmDialog
        open={!!pendingTeam}
        onClose={() => setPendingTeam(null)}
        onConfirm={() => {
          if (pendingTeam) doAssign(pendingTeam);
        }}
        title="Change team assignment?"
        message={
          selected && pendingTeam
            ? `${selected.firstName} ${selected.lastName} is currently on the ${
                selected.currentTeam === "army" ? "Army" : "Navy"
              } team. Are you sure you want to switch them to ${
                pendingTeam === "army" ? "Army" : "Navy"
              }?`
            : ""
        }
        confirmLabel={
          pendingTeam
            ? `Switch to ${pendingTeam === "army" ? "Army" : "Navy"}`
            : "Confirm"
        }
        variant="primary"
        loading={assigning}
      />
    </div>
  );
}
